import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { QUICK_DESTINATIONS } from "@shared/qrCodes";
import { useCreateQrCode, qrErrorMessage, type QrCodeRow } from "@/hooks/useQrCodes";
import { EntityPicker, type EntityRef } from "./EntityPicker";
import { QrImage, QrDownloads } from "./QrImage";

export function NewQrCodeDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreated?: (row: QrCodeRow) => void;
}) {
  const [name, setName] = useState("");
  const [destination, setDestination] = useState("/");
  const [entity, setEntity] = useState<EntityRef | null>(null);
  const [created, setCreated] = useState<QrCodeRow | null>(null);
  const create = useCreateQrCode();

  const reset = () => { setName(""); setDestination("/"); setEntity(null); setCreated(null); };
  const close = (o: boolean) => { if (!o) reset(); onOpenChange(o); };

  const submit = async () => {
    try {
      const row = await create.mutateAsync({
        name,
        destination,
        ...(entity?.kind === "contact" ? { contactId: entity.id } : {}),
        ...(entity?.kind === "company" ? { companyId: entity.id } : {}),
      });
      setCreated(row);
      onCreated?.(row);
    } catch (e: any) {
      toast.error(qrErrorMessage(e, "Could not create the QR code"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{created ? "QR code ready" : "New QR code"}</DialogTitle>
          <DialogDescription>
            {created
              ? "Download the SVG for print. You can change where it points later and the printed code keeps working."
              : "Every scan is counted on the Website tab."}
          </DialogDescription>
        </DialogHeader>

        {created ? (
          <div className="flex flex-col items-center gap-3">
            <QrImage url={created.url} size={208} />
            <code className="text-xs text-muted-foreground">{created.url}</code>
            <QrDownloads url={created.url} name={created.name} />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="qr-name">Name</Label>
              <Input id="qr-name" placeholder="OKC booth, spring 2026" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="qr-dest">Sends people to</Label>
              <Input id="qr-dest" value={destination} onChange={(e) => setDestination(e.target.value)} />
              <div className="flex flex-wrap gap-1.5">
                {QUICK_DESTINATIONS.map((d) => (
                  <button
                    key={d.path}
                    type="button"
                    onClick={() => setDestination(d.path)}
                    className="rounded border border-border px-2 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Tie to a person or company</Label>
              <EntityPicker value={entity} onChange={setEntity} />
              <p className="text-xs text-muted-foreground">
                Optional. Their scans show on their timeline and you get a notification, at most once a day.
                Anyone who scans this code counts as them.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          {created ? (
            <Button onClick={() => close(false)}>Done</Button>
          ) : (
            <Button onClick={submit} disabled={!name.trim() || create.isPending}>
              {create.isPending ? "Creating…" : "Create QR code"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
